// Copyright (c) Zammo Inc.
import {
  Components,
  createDirectLine,
  createCognitiveServicesSpeechServicesPonyfillFactory,
  createStore,
} from 'botframework-webchat';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactGA from 'react-ga4';

import { getVoiceList, Voice, VoiceNamePair } from '../../app/actions';
import { uuidv4 } from '../../utils/helpers';

import CustomDictationInterims from './CustomDictationInterims';
import CustomMicrophoneButton from './CustomMicrophoneButton';
import LastBotActivity from './LastBotActivity';
import {
  CustomDictationInterimsContainer,
  LastBotActivityContainer,
  MicrophoneButtonContainer,
  VoiceSimulatorContainer,
  Control,
  ChatWrapper,
} from './styles';
import VoiceChatHistory, { MessageSummary } from './VoiceChatHistory';
import VoiceSimulatorControl from './VoiceSimulatorControl';

const { Composer } = Components;

interface Props {
  directLineToken: string;
  simulatorToken: string;
  speechToken: string;
  speechRegion: string;
  speechVoiceNames: VoiceNamePair[];
}

export type MicrosoftVoice = {
  name: string;
};

/**
 * Defines the default voice that will be use when the conversation begins or if the user selected Voice does not exist.
 */
const DEFAULT_DIRECTLINE_SPEECH_VOICE_URI =
  'Microsoft Server Speech Text to Speech Voice (en-US, JennyMultilingualNeural)';

const VoiceOnlySimulator = ({
  directLineToken,
  simulatorToken,
  speechToken,
  speechRegion,
  speechVoiceNames,
}: Props) => {
  const id = useRef('dl_' + uuidv4()).current;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [directLine, setDirectLine] = useState<any>();
  const [isCaptionActive, setIsCaptionActive] = useState(true);
  const [isChatActive, setIsChatActive] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [speechPonyfill, setSpeechPonyfill] = useState<any>();
  const [currentSpeechRecognitionLanguage, setCurrentSpeechRecognitionLanguage] = useState('en-US');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [store, setStore] = useState<any>();
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>(
    speechVoiceNames?.find((v) => v.locale === 'en-US')?.directLineSpeechVoiceUri ?? DEFAULT_DIRECTLINE_SPEECH_VOICE_URI
  );
  const [allVoices, setAllVoices] = useState<Voice[]>([]);

  /**
   *
   * @param {MicrosoftVoice[]} voices the list of Microsoft voices that the Webchat uses when doing TTS
   * @returns {MicrosoftVoice} the voice to use for TTS
   */
  const selectVoice = (voices: MicrosoftVoice[]) => {
    const chosenVoice = voices?.find((v: MicrosoftVoice) => v.name === selectedVoice);

    if (chosenVoice) {
      return chosenVoice;
    }

    return voices?.find((v: MicrosoftVoice) => v.name === DEFAULT_DIRECTLINE_SPEECH_VOICE_URI);
  };

  useEffect(() => {
    if (speechRegion && speechToken) {
      getVoiceList(speechToken, speechRegion).then((data: Voice[]) => {
        data.sort((voiceA: Voice, voiceB: Voice) => (voiceA.Locale > voiceB.Locale ? 1 : -1));
        const mapped = data.filter((voice: Voice) => voice.VoiceType === 'Neural');

        setAllVoices(mapped);
      });
    }
  }, [speechRegion, speechToken]);

  useEffect(() => {
    if (!directLineToken || !id || !simulatorToken || allVoices.length === 0) {
      return;
    }

    // eslint-disable-next-line no-underscore-dangle
    ReactGA._gtag('event', 'UserID_dimension', { 'User ID': id });
    // eslint-disable-next-line no-underscore-dangle
    ReactGA._gtag('event', 'page_view', {
      page_path: '/simulator/voice',
    });

    async function fetchToken() {
      const ddirectLine = createDirectLine({ token: directLineToken });
      ddirectLine
        .postActivity({
          from: { id: id }, // required (from.name is optional)
          type: 'event',
          text: 'launch',
          entities: [
            {
              type: 'SimulatorConfig',
              value: {
                token: simulatorToken,
                isChat: false,
              },
            },
          ],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any)
        .subscribe();
      setDirectLine(ddirectLine);
    }

    async function fetchSpeech() {
      const webSpeechPonyfillFactory = await createCognitiveServicesSpeechServicesPonyfillFactory({
        credentials: {
          authorizationToken: speechToken,
          region: speechRegion,
          //textNormalization: 'lexical',
        },
      });

      setSpeechPonyfill(() => webSpeechPonyfillFactory);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const myStore = createStore({}, () => (next: any) => (action: any) => {
      if (action?.payload?.activity?.type === 'trace') {
        return;
      }

      if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
        try {
          if (action.payload.activity.type === 'typing') {
            setIsBotResponding(true);
          }

          const isMessageActivityType = action.payload.activity.type === 'message';
          const isActivityFromUser = action.payload.activity.from?.role === 'user';
          const hasTheLocaleOfTheActivityChanged =
            action.payload.activity.locale && action.payload.activity.locale !== document.documentElement.lang;
          const hasNonEmptyActivityText = isMessageActivityType && action.payload.activity.text;
          const hasNonEmptyActivitySpeak = isMessageActivityType && action.payload.activity?.channelData?.speak;

          if (isMessageActivityType && (hasNonEmptyActivityText || hasNonEmptyActivitySpeak)) {
            setMessages((m: MessageSummary[]) => [
              ...m,
              {
                text: hasNonEmptyActivitySpeak
                  ? action.payload.activity.channelData.speak
                  : action.payload.activity.text,
                user: action.payload.activity.from?.role,
              },
            ]);
          }

          if (isMessageActivityType && hasTheLocaleOfTheActivityChanged && !isActivityFromUser) {
            const activityLocale = action.payload.activity.locale;
            setCurrentSpeechRecognitionLanguage(activityLocale);

            const currentLocale = document.documentElement.lang;
            if (currentLocale !== activityLocale) {
              // try and find the default voice specified from the business config
              const businessVoiceForLocale = speechVoiceNames?.find((v) => v.locale === activityLocale);
              if (businessVoiceForLocale) {
                setSelectedVoice(businessVoiceForLocale.directLineSpeechVoiceUri);
              } else {
                // else fallback to the default voice from the language for the MSFT list
                const msftVoiceForLocale = allVoices?.find((v) => v.Locale === activityLocale);
                if (msftVoiceForLocale) {
                  setSelectedVoice(msftVoiceForLocale.Name);
                }
              }
            }

            document.documentElement.lang = activityLocale;
          }

          if (action.payload.activity?.channelData?.UiBuilderIgnore) {
            // ignore activities returned as ignore by the backend
            return;
          }

          if (isMessageActivityType && !isActivityFromUser) {
            setIsBotResponding(false);
          }

          // eslint-disable-next-line no-empty
        } catch {}
      }

      return next(action);
    });
    setStore(myStore);

    fetchToken();
    fetchSpeech();

    // @see https://github.com/microsoft/BotFramework-WebChat/issues/3029#issuecomment-600914137
    myStore.dispatch({
      type: 'WEB_CHAT/START_SPEAKING',
    });
  }, [directLineToken, id, simulatorToken, allVoices]);

  const toggleChat = useCallback(() => {
    setIsChatActive((isChat) => !isChat);
  }, []);

  const toggleCaption = useCallback(() => {
    setIsCaptionActive((isCaption) => !isCaption);
  }, []);

  return (
    <>
      {directLine && speechPonyfill && (
        <>
          <Control>
            <VoiceSimulatorControl
              isCaptionActive={isCaptionActive}
              isChatActive={isChatActive}
              toggleCaption={toggleCaption}
              toggleChat={toggleChat}
              setSelectedVoice={setSelectedVoice}
              selectedVoice={selectedVoice}
              allVoices={allVoices}
            />
          </Control>
          <VoiceSimulatorContainer>
            {isChatActive && <VoiceChatHistory messages={messages} />}
            <Composer
              directLine={directLine}
              webSpeechPonyfillFactory={speechPonyfill}
              selectVoice={selectVoice}
              store={store}
              locale={currentSpeechRecognitionLanguage}
            >
              <ChatWrapper>
                <LastBotActivityContainer isVisible={isCaptionActive && !isChatActive}>
                  <LastBotActivity className="App-bot-activity" />
                </LastBotActivityContainer>
                <CustomDictationInterimsContainer isVisible={isCaptionActive && !isChatActive}>
                  <CustomDictationInterims className="App-speech-interims" />
                </CustomDictationInterimsContainer>
              </ChatWrapper>
              <MicrophoneButtonContainer isChatActive={isChatActive}>
                <CustomMicrophoneButton
                  isBotResponding={isBotResponding}
                  isChatActive={isChatActive}
                  className="App-speech-button"
                />
              </MicrophoneButtonContainer>
            </Composer>
          </VoiceSimulatorContainer>
        </>
      )}
    </>
  );
};

export default memo(VoiceOnlySimulator);
